import { Component, OnDestroy, OnInit, Signal, TemplateRef, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, ParamMap } from "@angular/router";
import { AggregateManagerService } from "@tylertech/aggregates-module";
import { ChartManagerService } from "@tylertech/charting-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { FeatureFlagHelperService, NavMenuService, SecurityService, UserProfileService } from "@tylertech/core-module";
import { ShellService } from "@tylertech/incode-module";
import { IAggregate, IChartCriteria } from "@tylertech/tyler-api-types";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

const fifteenMinutes = 15 * 60 * 1000; // 900000;

@Component({
    selector: "dashboard",
    templateUrl: "./dashboard.tmpl.html",
    styleUrls: ["./dashboard.component.scss"],
    standalone: false
})
export class DashboardComponent implements OnDestroy, OnInit
{
    private readonly destructor$ = new Subject<void>();
    readonly title = "Dashboard";
    readonly page: number = 1;

    classAnimation: string = "rubberBand";
    purchasingLicense: boolean = true;
    isProcessing: boolean;
    fetched: boolean;
    timeElapsed: boolean;
    chartsLoaded: boolean;
    leftCharts: Array<IChartCriteria>;
    rightCharts: Array<IChartCriteria>;
    chartAggregates: IAggregate[];

    @ViewChild("ieWarning")
    private readonly ieWarningTemplate: TemplateRef<HTMLElement>;

    readonly devFlag: Signal<boolean>;

    constructor(
        private readonly $securityService: SecurityService,
        private readonly $userProfileService: UserProfileService,
        private readonly $navMenuService: NavMenuService,
        private readonly $commonDialogsService: CommonDialogsService,
        private readonly $shellService: ShellService,
        private readonly $chartManagerService: ChartManagerService,
        private readonly $aggregateManagerService: AggregateManagerService,
        private readonly matDialog: MatDialog,
        featureFlagService: FeatureFlagHelperService,
        activatedRoute: ActivatedRoute)
    {
        activatedRoute.paramMap.subscribe(
            (params: ParamMap) =>
            {
                if (params.has("module"))
                {
                    // TODO(DO-4549) - We never read this data here. So who are we "communicating" with? And why are we doing it through session storage?
                    window.sessionStorage.removeItem("current_module");
                    window.sessionStorage.removeItem("module_display");

                    this.$navMenuService.setDisabled(true);
                }
            },
            error => console.error(error)
        );

        $chartManagerService.leftChartsObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((charts: Array<IChartCriteria>) =>
            {
                this.leftCharts = charts;
            });

        $chartManagerService.rightChartsObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((charts: Array<IChartCriteria>) =>
            {
                this.rightCharts = charts;
            });

        $chartManagerService.chartsLoadedObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe(() =>
            {
                this.chartsLoaded = true;
            });

        this.devFlag = featureFlagService.DevFlag;
    }

    // TODO - why is this hard-coded here?
    private get isHrAnalyst(): boolean { return this.$securityService.hasPersona("HR Analyst"); }
    // TODO - why is this hard-coded here?
    private get isHrDirector(): boolean { return this.$securityService.hasPersona("HR Director"); }
    // TODO - why is this hard-coded here?
    private get isPyAnalyst(): boolean { return this.$securityService.hasPersona("Payroll Analyst"); }
    // TODO - why is this hard-coded here?
    private get isPySupervisor(): boolean { return this.$securityService.hasPersona("Payroll Supervisor"); }

    get embedded(): boolean { return this.$shellService.isEmbedded(); }
    get aggregatesLoaded(): boolean { return this.$aggregateManagerService.AggregatesLoaded; }
    get aggregateChartsLoaded(): boolean { return this.$aggregateManagerService.ChartAggregatesLoaded; }

    // TODO - why is this hard-coded here?
    get showEmployeeLookup(): boolean
    {
        return this.isHrDirector
            || this.isHrAnalyst
            || this.isPyAnalyst
            || this.isPySupervisor;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        this.refresh()
            .finally(() =>
            {
                this.fetched = true;
            });

        this.checkBrowser();
        window.opener = null;
    }

    private checkBrowser(): void
    {
        const userAgent = window.navigator.userAgent;
        if (userAgent.indexOf("MSIE ") > 0 || userAgent.indexOf("Trident/") > 0)
        {
            this.launchIEWarning();
        }
    }

    private launchIEWarning(): void
    {
        this.matDialog.open(this.ieWarningTemplate);
    }

    refresh(): Promise<void>
    {
        this.timeElapsed = false;
        return Promise.all(
            [
                this.$aggregateManagerService.RefetchAggregates(),
                this.$chartManagerService.updateCharts()
            ])
            .then(() =>
            {
                setTimeout(
                    () =>
                    {
                        this.timeElapsed = true;
                    },
                    fifteenMinutes);
            });
    }

    getChartAggregates(column: number): IAggregate[]
    {
        return this.$aggregateManagerService.GetChartAggregatesForColumn(column);
    }

    displayChartOrdering(): void
    {
        const groups = [
            this.leftCharts,
            this.rightCharts
        ];
        this.$commonDialogsService.showDragAndDropDialog(
            groups, "Title", "Application", "Dashboard Configuration", "Smart Charts")
            .then(
                r =>
                {
                    if (r)
                        this.saveChartSettings(r);
                },
                () =>
                {
                    window.location.reload();
                }
            );
    }

    private saveChartSettings(groups: IChartCriteria[][]): void
    {
        this.$userProfileService.SetPositions(groups)
            .finally(() =>
            {
                this.isProcessing = false;
            });
    }
}
